import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Stack,
  Grid2,
  CircularProgress,
  Button,
  TextField,
  InputAdornment,
  Alert,
} from '@mui/material';
import { Navigate } from 'react-router-dom';
import { Iconify } from '../Minimal/src/components/iconify';
import config from '../common/Config';
import { isBritWholesaleUi } from '../../utils/britWholesaleUi';
import '../common/commonStyles.css';

const apiBase = () => String(config.API_URL || '').replace(/\/+$/, '');

const csvEscape = (v) => {
  const s = String(v ?? '');
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const PAGE_TITLE = 'Brit dual stock';

const BritDualStockReportInner = () => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [listMessage, setListMessage] = useState('');
  const [error, setError] = useState('');
  const accessToken = localStorage.getItem('accessToken');

  const fetchReport = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(rowsPerPage),
        search: appliedSearch,
      });
      const res = await fetch(`${apiBase()}/inventory/ws-brit-dual-stock?${params.toString()}`, {
        method: 'GET',
        headers: { authorization: `Bearer ${accessToken}` },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setData([]);
        setTotalCount(0);
        setListMessage('');
        setError(json.message || json.error || 'Request failed.');
        return;
      }
      if (!json.success) {
        setData([]);
        setTotalCount(0);
        setListMessage('');
        setError(json.message || 'Request failed.');
        return;
      }
      const rows = Array.isArray(json.data) ? json.data : [];
      setData(rows);
      setListMessage(json.message || '');
      const meta = json.meta || {};
      const t = meta.total;
      setTotalCount(typeof t === 'number' && !Number.isNaN(t) ? t : rows.length);
    } catch {
      setData([]);
      setTotalCount(0);
      setListMessage('');
      setError('Network error.');
    } finally {
      setLoading(false);
    }
  }, [accessToken, page, rowsPerPage, appliedSearch]);

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    fetchReport();
  }, [accessToken, fetchReport]);

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setPage(0);
      setAppliedSearch(searchInput.trim());
    }
  };

  const handleSubmit = () => {
    setPage(0);
    setAppliedSearch(searchInput.trim());
  };

  const handleChangePage = (e, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const exportCsv = () => {
    const headers = [
      'Code',
      'Name',
      'Stock',
      'Cash Stock',
      'Total Stock',
    ];
    const lines = [headers.map(csvEscape).join(',')];
    data.forEach((row) => {
      const br = row.ebusso_brit || {};
      const ws = row.ebusso_brit_wholesale || {};
      const bs = br.stock || {};
      const wss = ws.stock || {};
      lines.push(
        [
          row.code || br.code || ws.code || '',
          br.name || ws.name || '',
          bs.quantity ?? '',
          wss.quantity ?? '',
          Number(bs.quantity ?? 0) + Number(wss.quantity ?? 0),
        ]
          .map(csvEscape)
          .join(',')
      );
    });
    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'brit-dual-stock.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box className="page-container">
      <Paper className="paper-style">
        <Box sx={{ px: 2, py: 1.2 }}>
          <Typography className="page-title" mb={0.5}>
            {PAGE_TITLE}
          </Typography>
          {listMessage && listMessage.trim().toLowerCase() !== PAGE_TITLE.toLowerCase() ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {listMessage}
            </Typography>
          ) : null}
          {error ? (
            <Alert severity="error" sx={{ mb: 1 }}>
              {error}
            </Alert>
          ) : null}
          <Grid2 container spacing={2} alignItems="flex-end">
            <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                className="input-border"
                size="small"
                placeholder="Search code or name…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
              <Stack direction="row" spacing={1}>
                <Button
                  className="button-variant-contained"
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                >
                  Search
                </Button>
                <Button
                  className="button-variant-outlined"
                  variant="outlined"
                  onClick={exportCsv}
                  disabled={!data.length}
                  startIcon={<Iconify icon="eva:download-outline" width={18} />}
                >
                  Export CSV
                </Button>
              </Stack>
            </Grid2>
          </Grid2>
        </Box>

        <TableContainer>
          <Table size="medium" className="table-cell-border">
            <TableHead>
              <TableRow className="table-cell-header">
                <TableCell sx={{ width: 56 }}>Sr.</TableCell>
                <TableCell>Code</TableCell>
                <TableCell sx={{ minWidth: 200 }}>Name</TableCell>
                <TableCell align="right">I Stock</TableCell>
               
                <TableCell align="right">C Stock</TableCell>
                <TableCell align="right">Total Stock</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : data.length > 0 ? (
                data.map((row, index) => {
                  const br = row.ebusso_brit || {};
                  const ws = row.ebusso_brit_wholesale || {};
                  const bs = br.stock || {};
                  const wss = ws.stock || {};
                  const totalStock = Number(bs.quantity ?? 0) + Number(wss.quantity ?? 0);
                  return (
                    <TableRow key={`${row.code || index}-${index}`} hover className="table-cell-style">
                      <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{row.code || br.code || ws.code || ''}</TableCell>
                      <TableCell>{br.name || ws.name || '—'}</TableCell>
                      <TableCell align="right">{bs.quantity ?? '—'}</TableCell>
                    
                      <TableCell align="right">{wss.quantity ?? '—'}</TableCell>
                      <TableCell align="right">{totalStock}</TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#637381', fontSize: '0.75rem' }}>
                    No records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Rows per page:"
          className="pagination-styles"
        />
      </Paper>
    </Box>
  );
};

const BritDualStockReport = () => {
  if (!isBritWholesaleUi()) {
    return <Navigate to="/dashboard" replace />;
  }
  return <BritDualStockReportInner />;
};

export default BritDualStockReport;
